<template>
  <DropdownMenuRoot v-slot="{ open }">
    <DropdownMenuTrigger
      class="flex items-center justify-between gap-2"
      :class="triggerClass"
      :disabled="disabled || locked"
      :title="locked ? $t('source.locked-to-pos') : undefined"
    >
      <SourceInfo :source="selectedSource" />
      <FontAwesomeIcon v-if="!locked" :icon="open ? faChevronUp : faChevronDown" />
      <FontAwesomeIcon v-else :icon="faLocationPinLock" />
    </DropdownMenuTrigger>
    <DropdownMenuContent
      :side-offset="10"
      class="bg-base-300 max-h-96 overflow-y-auto rounded shadow"
      style="z-index: 10"
    >
      <DropdownMenuRadioGroup
        :model-value="`${selectedSource?.id ?? ''}`"
        @update:model-value="selectSource"
      >
        <DropdownMenuRadioItem
          v-for="source in sources"
          :key="source.id"
          :value="`${source.id}`"
          :checked="selectedSource?.id === source.id"
          class="btn btn-soft btn-lg flex justify-start gap-4"
          :class="getSourceTypeClass(source.type, 'btn')"
        >
          <FontAwesomeIcon :icon="getSourceTypeIcon(source.type)" />
          <DropdownMenuItemIndicator>
            <FontAwesomeIcon :icon="faCheck" />
          </DropdownMenuItemIndicator>
          {{ source.name }}
        </DropdownMenuRadioItem>
        <DropdownMenuRadioItem
          v-if="allowGlobal"
          :key="'GLOBAL'"
          value="0"
          :checked="selectedSource?.id === null"
          class="btn btn-soft btn-lg flex justify-start gap-4"
          :class="getSourceTypeClass('global', 'btn')"
        >
          <FontAwesomeIcon :icon="faGlobe" />
          <DropdownMenuItemIndicator>
            <FontAwesomeIcon :icon="faCheck" />
          </DropdownMenuItemIndicator>
          {{ $t('source.global') }}
        </DropdownMenuRadioItem>
      </DropdownMenuRadioGroup>
      <DropdownMenuArrow class="fill-base-300 h-2 w-4" />
    </DropdownMenuContent>
  </DropdownMenuRoot>
</template>

<script setup lang="ts">
import {
  faCheck,
  faChevronDown,
  faChevronUp,
  faGlobe,
  faLocationPinLock,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { type RefSourceDto } from '@konsolenkost-de/acquisition_store_server_sdk_vue';
import {
  DropdownMenuArrow,
  DropdownMenuContent,
  DropdownMenuItemIndicator,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuRoot,
  DropdownMenuTrigger,
  type AcceptableValue,
} from 'reka-ui';
import type { ClassValue } from 'vue';
import { useSourceTypeInfo } from '../../composables/source/useSourceTypeInfo';
import { useSource } from '../../stores/sourceStore';
import SourceInfo from './SourceInfo.vue';

const props = withDefaults(
  defineProps<{
    triggerClass?: ClassValue;
    disabled?: boolean;
    /** Set false where a concrete source is required, e.g. a POS location. */
    allowGlobal?: boolean;
    locked?: boolean;
  }>(),
  {
    triggerClass: 'btn btn-neutral py-6',
    allowGlobal: true,
  },
);

const emit = defineEmits<{
  (e: 'update:modelValue', value: RefSourceDto | null): void;
}>();

const selectedSource = defineModel<RefSourceDto | null>({ required: true });

const { sources } = useSource();
const { getSourceTypeIcon, getSourceTypeClass } = useSourceTypeInfo('text');

const selectSource = async (sourceId: AcceptableValue) => {
  if (props.locked) return;

  const source = sources.value.find((s) => `${s.id}` === sourceId) ?? null;

  emit('update:modelValue', sourceId === '0' ? null : source);
};
</script>
